/**
 * The list's selection keys, in the table and in the cards. `listSelectionKeyAction` reads a
 * keydown and says what it does: Cmd-A selects every loaded row, Esc steps back along the record
 * pane's trail or clears the selection, and ↓ and ↑ move the one selected row. It returns null
 * for a key the list leaves alone, such as one typed into a field or one meant for an open menu.
 */

export type TableSelectionKeyAction = { kind: 'select'; rowId: string } | { kind: 'edge' } | null;

export const TYPING_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/**
 * Where ↓ or ↑ moves the selected row. Nothing happens while no row is selected,
 * so ↑ and ↓ still scroll the table then. At the first or last row it returns
 * `edge`: the key does not wrap around or change the page.
 */
export function tableSelectionKeyAction(key: string, rowIds: string[], selectedRowId: string | null): TableSelectionKeyAction {
    if (selectedRowId === null) return null;
    if (key !== 'ArrowDown' && key !== 'ArrowUp') return null;
    const index = rowIds.indexOf(selectedRowId);
    if (index === -1) return null;
    const nextIndex = key === 'ArrowDown' ? index + 1 : index - 1;
    if (nextIndex < 0 || nextIndex >= rowIds.length) return { kind: 'edge' };
    return { kind: 'select', rowId: rowIds[nextIndex] };
}

/**
 * What a key does to the list's selection. `move` selects only the next or previous row.
 * `stop` changes nothing, but the key is still the list's, so the page cancels it. That is an
 * arrow at the first or last row, which must not scroll the table either.
 */
export type ListSelectionKeyAction =
    | { kind: 'selectAll' }
    | { kind: 'clear' }
    | { kind: 'trailBack' }
    | { kind: 'move'; rowId: string }
    | { kind: 'stop' }
    | null;

export interface ListSelectionKeyContext {
    /** Whether the record pane has a trail that Esc can step back along. */
    hasTrail: boolean;
    /** Whether any row is selected. */
    hasSelection: boolean;
    /** The id of the one selected row, or null while none or several are selected. */
    soleSelectedId: string | null;
    /** The loaded rows' ids, in the order they are shown. */
    rowIds: string[];
    /** Whether ↑ and ↓ move the selection. Only the table allows them. */
    allowArrows: boolean;
    /** Whether Cmd-A selects every loaded row. Custom list cards leave it to the browser. */
    allowSelectAll: boolean;
    /** Whether the overlay inspector is open. Its keys are its own. */
    inspectorIsOpen: boolean;
}

const RECORD_PANE_SELECTOR = '[data-testid="record-pane"]';

/**
 * Whether the key belongs to something other than the list. That is so while someone types,
 * and while a menu, dialog, popover or the inspector is open.
 * There the browser or that layer handles the key, as today.
 */
function keyBelongsElsewhere(event: KeyboardEvent, inspectorIsOpen: boolean): boolean {
    if (event.defaultPrevented || inspectorIsOpen) return true;
    const target = event.target;
    if (target instanceof Element && target.closest(TYPING_SELECTOR)) return true;
    // Reka marks every open menu, popover and dialog this way. Its Escape is that layer's.
    return typeof document !== 'undefined' && document.querySelector('[data-dismissable-layer]') !== null;
}

/**
 * What a key does to the page's selection, in the table and in the cards.
 * Cmd-A (Ctrl-A on Windows) selects every loaded row.
 * Esc steps back along the pane's trail first, and clears the selection when there is no trail.
 * With focus in the record pane, Esc is the only one of these keys the list handles.
 * ↑ and ↓ move the selection while exactly one row is selected. At the first or last row they
 * return `stop`: they do not wrap around, change the page or scroll the table.
 */
export function listSelectionKeyAction(event: KeyboardEvent, context: ListSelectionKeyContext): ListSelectionKeyAction {
    if (keyBelongsElsewhere(event, context.inspectorIsOpen)) return null;

    // Inside the record pane the keys are the pane's, except a plain Esc. It steps back along the
    // trail, or clears the selection and so closes the pane, as it does today.
    const target = event.target;
    const plainEscape = event.key === 'Escape' && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    if (!plainEscape && target instanceof Element && target.closest(RECORD_PANE_SELECTOR)) return null;

    const commandHeld = event.metaKey || event.ctrlKey;
    if (commandHeld && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'a') {
        return context.allowSelectAll ? { kind: 'selectAll' } : null;
    }

    if (commandHeld || event.shiftKey || event.altKey) return null;

    if (event.key === 'Escape') {
        if (context.hasTrail) return { kind: 'trailBack' };
        return context.hasSelection ? { kind: 'clear' } : null;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return null;
    if (!context.allowArrows || context.soleSelectedId === null) return null;
    const action = tableSelectionKeyAction(event.key, context.rowIds, context.soleSelectedId);
    if (action?.kind === 'select') return { kind: 'move', rowId: action.rowId };
    return action?.kind === 'edge' ? { kind: 'stop' } : null;
}
